import React, { useMemo, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import {
  IAnomaliesV2SeriesPoint,
  TAnomaliesPrimaryMetric,
} from '../../shared/types'
import { varClass } from '../../shared'
import { formatDecimal } from '../shared'

const xTickFormatter = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

const driftPickerFor = (
  metric: TAnomaliesPrimaryMetric
): keyof IAnomaliesV2SeriesPoint | null => {
  if (metric === 'cr') return 'crDriftRatio'
  if (metric === 'epc') return 'epcDriftRatio'
  if (metric === 'spc') return 'spcDriftRatio'
  return null
}

const z7v30PickerFor = (
  metric: TAnomaliesPrimaryMetric
): keyof IAnomaliesV2SeriesPoint | null => {
  if (metric === 'cr') return 'cr7v30Z'
  if (metric === 'epc') return 'epc7v30Z'
  if (metric === 'spc') return 'spc7v30Z'
  if (metric === 'clicks') return 'clicks7v30Z'
  if (metric === 'conversions') return 'conversions7v30Z'
  if (metric === 'rejection_rate') return 'rejRate7v30Z'
  return null
}

const z7v90PickerFor = (
  metric: TAnomaliesPrimaryMetric
): keyof IAnomaliesV2SeriesPoint | null => {
  if (metric === 'cr') return 'cr7v90Z'
  if (metric === 'epc') return 'epc7v90Z'
  if (metric === 'spc') return 'spc7v90Z'
  if (metric === 'clicks') return 'clicks7v90Z'
  if (metric === 'conversions') return 'conversions7v90Z'
  // rejection_rate has no 7v90 z in the type — skip it.
  return null
}

// ---------------------------------------------------------------------------
// Drift ratio chart — current 30d / 6m anchor. Threshold = 0.70.
// Only renders when the metric has a drift ratio field on the series.
// ---------------------------------------------------------------------------
export const DriftRatioChart = (props: {
  series: IAnomaliesV2SeriesPoint[]
  metric: TAnomaliesPrimaryMetric
}) => {
  const { series, metric } = props
  const [open, setOpen] = useState(false)

  const picker = driftPickerFor(metric)
  const chartData = useMemo(
    () =>
      picker
        ? series.map((p) => ({
            date: p.eventDate,
            value: (p[picker] as number | null | undefined) ?? null,
          }))
        : [],
    [series, picker]
  )
  const hasData = chartData.some((d) => d.value != null && !isNaN(d.value))

  if (!picker) return null

  return (
    <ChartShell
      title="Drift ratio (current 30d / 6m baseline)"
      open={open}
      setOpen={setOpen}
      empty={
        !hasData
          ? 'No drift-ratio data for this metric in the selected range.'
          : null
      }
      threshold={0.7}
      thresholdLabel="Threshold 0.70"
      seriesLabel="Drift ratio"
      chartData={chartData}
    />
  )
}

// ---------------------------------------------------------------------------
// Z-score chart — 7v30 default with a 7v90 toggle when available.
// Threshold = -2.5 (acute) drawn as a guide.
// ---------------------------------------------------------------------------
export const ZScoreChart = (props: {
  series: IAnomaliesV2SeriesPoint[]
  metric: TAnomaliesPrimaryMetric
}) => {
  const { series, metric } = props
  const [open, setOpen] = useState(false)
  const [window, setWindow] = useState<'7v30' | '7v90'>('7v30')

  const picker7v30 = z7v30PickerFor(metric)
  const picker7v90 = z7v90PickerFor(metric)
  const picker = window === '7v30' ? picker7v30 : picker7v90

  const chartData = useMemo(
    () =>
      picker
        ? series.map((p) => ({
            date: p.eventDate,
            value: (p[picker] as number | null | undefined) ?? null,
          }))
        : [],
    [series, picker]
  )
  const hasData = chartData.some((d) => d.value != null && !isNaN(d.value))

  if (!picker7v30 && !picker7v90) return null

  return (
    <ChartShell
      title="Z-score"
      open={open}
      setOpen={setOpen}
      empty={
        !hasData
          ? 'No z-score data for this metric/window. Try the other window above, or check Detector summary.'
          : null
      }
      threshold={-2.5}
      thresholdLabel="Threshold −2.5"
      seriesLabel={`Z-score (${window})`}
      chartData={chartData}
      headerExtra={
        <div className="row row-center" style={{ gap: 8 }}>
          <span className="text-light text-small">Window:</span>
          <div className="anomalies-trend-toggle-group">
            <button
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': window === '7v30',
              })}
              onClick={() => setWindow('7v30')}
              disabled={!picker7v30}
            >
              7v30
            </button>
            <button
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': window === '7v90',
              })}
              onClick={() => setWindow('7v90')}
              disabled={!picker7v90}
            >
              7v90
            </button>
          </div>
        </div>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Shared card shell used by both charts. Renders the toggle, optional header
// controls, empty state, and the line+threshold chart itself.
// ---------------------------------------------------------------------------
const ChartShell = (props: {
  title: string
  open: boolean
  setOpen: (open: boolean) => void
  empty: string | null
  threshold: number
  thresholdLabel: string
  seriesLabel: string
  chartData: { date: string; value: number | null }[]
  headerExtra?: React.ReactNode
}) => {
  const {
    title,
    open,
    setOpen,
    empty,
    threshold,
    thresholdLabel,
    seriesLabel,
    chartData,
    headerExtra,
  } = props

  return (
    <div className="card anomalies-chart anomalies-evidence-chart">
      <div className="card-body p-t-1">
        <div className="row row-space-between row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={varClass({
              'anomalies-evidence-toggle': true,
              'open': open,
            })}
            onClick={() => setOpen(!open)}
          >
            <span className="text-dark text-bolder">{title}</span>
            <span className="text-light text-small">{open ? 'Hide' : 'Show'}</span>
          </button>
          {open && headerExtra}
        </div>

        {open && empty && (
          <div className="text-light text-small p-y-6 text-center">{empty}</div>
        )}
        {open && !empty && (
          <div className="m-t-3">
            <ResponsiveContainer width="99.9%" height={200}>
              <ComposedChart
                data={chartData}
                margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
              >
                <CartesianGrid
                  horizontal
                  vertical={false}
                  strokeDasharray="6"
                  stroke="#EDEFF2"
                />
                <ReferenceLine
                  y={threshold}
                  stroke="#E55353"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    value: thresholdLabel,
                    fill: '#E55353',
                    fontSize: 11,
                    position: 'right',
                  }}
                />
                <Tooltip content={<EvidenceTooltip label={seriesLabel} />} />
                <Line
                  dataKey="value"
                  stroke="#4F9EEC"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  tickFormatter={xTickFormatter}
                  tick={{ fontSize: 11, fill: '#828B9E' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  tick={{ fontSize: 11, fill: '#828B9E' }}
                  domain={[
                    (dataMin: number) => Math.min(dataMin, threshold) - 0.5,
                    (dataMax: number) => Math.max(dataMax, threshold) + 0.5,
                  ]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

const EvidenceTooltip = (props: any) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  const value = row.value
  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: '#4F9EEC' }}
        />
        <span className="anomalies-chart-tooltip-label">{props.label}</span>
        <span className="anomalies-chart-tooltip-value">
          {formatDecimal(value, 2)}
        </span>
      </div>
    </div>
  )
}
